import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { CustomButton } from "@/components/common/customButton";
import { useReferenceLists } from "@/context/GlobalGetLists";
import type { KillReason } from "@/types/deal";

type KillReasonDialogProps = {
  open: boolean;
  dealName: string;
  onCancel: () => void;
  /** null: kill without a reason (it then shows in the lead's data check). */
  onConfirm: (reason: KillReason | null) => void;
};

/**
 * Asks for a kill reason when a deal is set to Killed. The user can still
 * kill it without one; the missing reason is flagged by the data check.
 */
export function KillReasonDialog({
  open,
  dealName,
  onCancel,
  onConfirm,
}: KillReasonDialogProps) {
  const { killReasons } = useReferenceLists();
  const [reasonId, setReasonId] = useState<number | "">("");

  useEffect(() => {
    if (open) setReasonId("");
  }, [open]);

  const reason = killReasons.find((r: KillReason) => r.id === reasonId);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Why was this deal killed?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Choose why <strong>{dealName}</strong> was killed. You can kill it
          without a reason, but it will show as missing data until one is
          added.
        </DialogContentText>
        <TextField
          select
          fullWidth
          size="small"
          label="Kill reason"
          value={reasonId}
          onChange={(e) => setReasonId(Number(e.target.value))}
          slotProps={{ htmlInput: { "data-testid": "kill-reason-select" } }}
        >
          {killReasons.map((r: KillReason) => (
            <MenuItem key={r.id} value={r.id}>
              {r.kill_reason}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <CustomButton
          handleButtonPress={onCancel}
          isButtonDisabled={false}
          text="Cancel"
          solid={false}
        />
        <CustomButton
          handleButtonPress={() => onConfirm(null)}
          isButtonDisabled={false}
          text="Kill without reason"
          solid={false}
        />
        <CustomButton
          handleButtonPress={() => reason && onConfirm(reason)}
          isButtonDisabled={!reason}
          text="Kill deal"
          solid
        />
      </DialogActions>
    </Dialog>
  );
}
